




















































































import mixins from 'vue-typed-mixins';
import { AxiosResponse } from 'axios';
import { JSONEditorOptions } from 'jsoneditor';
import LocationBasedMixin from '@/Shared/LocationBasedMixin';
import { supportedLocales } from '@/internationalization';
import { LocalizationMessages } from '@/internationalization/en-us';
import { LocalizationOverride } from './LocalizationOverride';
import buildEntityListComponentMixin from '@/Shared/CommonEntityMixins/EntityListComponentMixin';
import { deepMerge } from '@/Shared/Utilities';

export default mixins(LocationBasedMixin, buildEntityListComponentMixin<LocalizationOverride>()).extend({
    components: {
        JSONEditor: () => import('@/Shared/JsonEditor.vue'),
    },
    computed: {
        entityUrls() {
            return `/${this.location}/Localizations/Overrides`;
        },
        availableLocales() {
            let alreadyOverridden: string[] = this.entities.map(item => item.cultureName);
            if (this.editItem) {
                alreadyOverridden = alreadyOverridden.filter(item => item !== this.editItem!.cultureName);
            }

            const available = Object.keys(supportedLocales)
                .filter(key => !alreadyOverridden.includes(supportedLocales[key]));
            return available;
        }
    },
    data() {
        return {
            i18nPrefix: 'admin.localizationOverrides',
            options: {
                mode: 'form'
            } as JSONEditorOptions,
            supportedLocales,
            showOverridesOnly: false,
            overridesOnly: null as any as LocalizationMessages,
        };
    },
    methods: {
        afterLoad(response: AxiosResponse<LocalizationOverride[]>) {
            for (const override of response.data) {
                const overrideValues = JSON.parse(override.overrides as string);
                const existing = this.$i18n.messages[override.cultureName];
                const merged = deepMerge({}, existing as any, overrideValues as any);
                override.overrides = merged as LocalizationMessages;
            }
            return response.data;
        },
        toggleOverridesOnly() {
            this.showOverridesOnly = !this.showOverridesOnly;
            if (!this.editItem) {
                return;
            }
            if (this.showOverridesOnly) {
                this.overridesOnly = this.getDiff(this.editItem);
            }
            else {
                const merged = deepMerge({}, this.editItem.overrides as any, this.overridesOnly as any);
                this.editItem.overrides = merged as LocalizationMessages;
            }
        },
        saveOverride(override: LocalizationOverride) {
            // duplicate it so that the stringification doesn't mess with
            // the editor while we're saving
            const duplicate: LocalizationOverride = {
                ...override
            };
            const diff = this.showOverridesOnly
                ? this.overridesOnly
                : this.getDiff(duplicate);
            duplicate.overrides = JSON.stringify(diff) as LocalizationMessages;
            this.saveEntity(duplicate);
        },
        afterSave(response: AxiosResponse<LocalizationOverride>) {
            this.showOverridesOnly = false;
            this.overridesOnly = null as any;

            response.data.overrides = JSON.parse(response.data.overrides as string);
            const localeName = `${response.data.cultureName}-${this.location}`;
            this.$i18n.setLocaleMessage(localeName, response.data.overrides as any);
            const existing = this.$i18n.messages[response.data.cultureName] as any;
            const overridden = deepMerge({}, existing, response.data.overrides as any);
            response.data.overrides = overridden;
            return response.data;
        },
        deleteOverride(override: LocalizationOverride) {
            const submit = {
                ...override
            };
            submit.overrides = JSON.stringify(override.overrides) as any;
            this.deleteEntity(submit);
        },
        addOverride() {
            this.add();
            this.editItem = {
                cultureName: null as unknown as string,
                overrides: {},
            } as LocalizationOverride;
        },
        cultureChanged(entity: LocalizationOverride) {
            const existing = this.$i18n.messages[entity.cultureName];
            const merged = deepMerge({}, existing as any, this.overridesOnly as any);
            entity.overrides = merged as LocalizationMessages;
        },
        localeName(cultureName: string) {
            for (const name in supportedLocales) {
                if (!supportedLocales[name]) {
                    continue;
                }
                if (supportedLocales[name] === cultureName) {
                    return name;
                }
            }
            return this.$t('common.unknown');
        },
        getDiff(override: LocalizationOverride): LocalizationMessages {
            const localeMessages = this.$i18n.messages[override.cultureName];
            const recursive = (overrideMessages: any, existing: any): LocalizationMessages => {
                const diff = {} as any;
                for (const messageKey in overrideMessages) {
                    if (!overrideMessages[messageKey]
                        || !existing[messageKey]) {
                        continue;
                    }

                    if (typeof overrideMessages[messageKey] === 'object') {
                        const childDiff = recursive(overrideMessages[messageKey], existing[messageKey]);
                        if (Object.keys(childDiff).length) {
                            diff[messageKey] = childDiff;
                        }
                    }
                    else if (overrideMessages[messageKey] !== existing[messageKey]) {
                        diff[messageKey] = overrideMessages[messageKey];
                    }
                }
                return diff as LocalizationMessages;
            };

            return recursive(override.overrides, localeMessages as LocalizationMessages);
        }
    }
});
